import { Link, Outlet, useRouterState } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { Suspense, useEffect, useMemo, useState } from 'react'
import { List, SidebarSimple } from '@phosphor-icons/react'
import { useTranslation } from 'react-i18next'
import { useBrandAuthenticatedSnapshot, useMergedBrandShellAppearance } from '@/shared/api/brandQueries'
import { CommandPalette } from '@/shared/components/cse'
import { PointsBalanceBadge } from '@/shared/components/PointsBalanceBadge'
import { UrgentMessagesHost } from '@/shared/components/UrgentMessagesHost'
import { CookieBanner } from '@/shared/components/legal/CookieBanner'
import { openCookiePreferences } from '@/shared/lib/cookieConsent'
import { LEGAL_PAGE_SLUGS, type LegalPageSlug } from '@/shared/constants/legalPageSlugs'
import { Button } from '@/shared/components/ui/button'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/components/ui/sheet'
import { cn } from '@/shared/lib/utils'
import { RouteFallback } from '@/shared/components/RouteFallback'
import {
  parseAuthenticatedPortalNavigation,
  type PortalNavigationItem,
} from '@/shared/schemas/brand.schema'
import { PortalSidebar } from '@/shared/components/PortalSidebar'
import { resolveAssetUrl } from '@/shared/lib/resolveAssetUrl'
import { SIDEBAR_COLLAPSE_KEY, readSidebarCollapsed } from '@/shared/lib/sidebarCollapse'
import { V3_SIDEBAR_SHELL } from '@/shared/config/shellLayout'
import { portalNavAriaCurrentPage } from '@/shared/lib/portalNavAriaCurrent'
import { createLazyThemedIcon } from '@/shared/theme/resolveThemedIcon'
import { ThemeSlot } from '@/shared/theme'
import { BottomNav } from './BottomNav'
import { PortalNavLinks } from './PortalNavLinks'

const ExternalIcon = createLazyThemedIcon('arrow-external')

function sortNav(items: PortalNavigationItem[]): PortalNavigationItem[] {
  return [...items].sort((a, b) => a.sort - b.sort)
}

function flattenFooterNav(items: PortalNavigationItem[]): PortalNavigationItem[] {
  return sortNav(items).flatMap((item) =>
    item.link_type === 'group' ? sortNav(item.children).filter((child) => child.enabled) : [item],
  )
}

export function AppLayout({ headerActions }: { headerActions?: ReactNode }) {
  const { t } = useTranslation()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [menuOpen, setMenuOpen] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed)
  const brandMerged = useMergedBrandShellAppearance()
  const authBrandSnapshot = useBrandAuthenticatedSnapshot()

  const configuredHeaderNav = useMemo(() => {
    const items = sortNav(
      parseAuthenticatedPortalNavigation(authBrandSnapshot.data).filter(
        (i) => i.enabled && i.placement === 'header_primary',
      ),
    )
    if (items.length === 0) {
      return null
    }

    return items
  }, [authBrandSnapshot.data])

  const footerSecondaryNav = useMemo(() => {
    return flattenFooterNav(
      parseAuthenticatedPortalNavigation(authBrandSnapshot.data).filter(
        (i) => i.enabled && i.placement === 'footer_legal_secondary',
      ),
    )
  }, [authBrandSnapshot.data])

  const brandName =
    brandMerged?.name?.trim() !== '' && brandMerged?.name != null
      ? brandMerged.name
      : t('home.brandFallback')

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev
      if (typeof window !== 'undefined') window.localStorage.setItem(SIDEBAR_COLLAPSE_KEY, next ? '1' : '0')
      return next
    })
  }

  const closeMobile = () => setMenuOpen(false)
  const showLoginLink = headerActions == null
  const srExternal = t('app.layout.external_link_sr')
  const [headerScrolled, setHeaderScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setHeaderScrolled(window.scrollY > 0)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <div className="horizon-page horizon-shell relative flex min-h-dvh flex-col bg-background text-foreground">
      <CookieBanner />
      <a
        href="#main-content"
        className={cn(
          'absolute left-[-10000px] top-4 z-[var(--brand-z-critical)] rounded-[var(--brand-radius-md)] bg-[var(--brand-primary-strong)] px-4 py-2 text-sm font-medium text-[var(--brand-on-primary)] no-underline outline-none transition-none focus:left-4 focus:inline-block focus:outline-none focus:ring-2 focus:ring-[var(--brand-ring)] focus:ring-offset-2 focus:ring-offset-background',
        )}
      >
        {t('legal.skip_to_content')}
      </a>

      <UrgentMessagesHost />

      <div className={cn('flex flex-1 flex-col', V3_SIDEBAR_SHELL && 'lg:flex-row')}>
        {V3_SIDEBAR_SHELL ? (
          <PortalSidebar
            headerConfigured={configuredHeaderNav}
            collapsed={sidebarCollapsed}
            onToggle={toggleSidebar}
            className="hidden lg:flex"
          />
        ) : null}
        <div className="flex min-w-0 flex-1 flex-col">
          <ThemeSlot
            name="shell.header"
            fallback={
          <header
            role="banner"
            data-scrolled={headerScrolled ? 'true' : 'false'}
            className="border-b border-[var(--brand-border-soft)] bg-[var(--brand-background)]"
          >
            <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
              {V3_SIDEBAR_SHELL ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="hidden shrink-0 lg:inline-flex"
                  aria-pressed={sidebarCollapsed}
                  onClick={toggleSidebar}
                >
                  <SidebarSimple className="size-5" aria-hidden />
                  <span className="sr-only">
                    {sidebarCollapsed ? t('app.layout.sidebar_expand') : t('app.layout.sidebar_collapse')}
                  </span>
                </Button>
              ) : null}
              <Link to="/" className="flex items-center gap-3 text-[var(--brand-text-primary)] no-underline" onClick={closeMobile}>
                {brandMerged?.logo_url ? (
                  <img
                    src={resolveAssetUrl(brandMerged.logo_url)}
                    alt={t('a11y.logo.alt', { brand: brandName })}
                    className="h-auto max-h-9 w-auto max-w-32 object-contain"
                  />
                ) : (
                  <span className="font-display text-base leading-none">{brandName}</span>
                )}
              </Link>

              <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
                <div>
                  <CommandPalette />
                </div>
                {headerActions ? (
                  <div>
                    <PointsBalanceBadge />
                  </div>
                ) : null}
                {headerActions ? <div className="flex shrink-0 items-center gap-2">{headerActions}</div> : null}
                <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="shrink-0 lg:hidden"
                    aria-expanded={menuOpen}
                    aria-controls="app-mobile-sheet"
                    onClick={() => setMenuOpen(true)}
                  >
                    <List className="size-5" aria-hidden />
                    <span className="sr-only">{t('app.layout.menu_open')}</span>
                  </Button>
                  <SheetContent
                    id="app-mobile-sheet"
                    side="right"
                    sheetCloseLabel={t('app.layout.sheet_close')}
                    className="p-0 pt-12 pb-[env(safe-area-inset-bottom)]"
                  >
                    <SheetHeader className="border-b border-[var(--brand-border-soft)] px-6 pb-4 text-start">
                      <SheetTitle>{t('app.layout.sheet_nav_title')}</SheetTitle>
                    </SheetHeader>
                    <PortalNavLinks
                      variant="sheet"
                      onNavigate={closeMobile}
                      showLoginLink={showLoginLink}
                      headerConfigured={configuredHeaderNav}
                    />
                  </SheetContent>
                </Sheet>

                {V3_SIDEBAR_SHELL ? null : (
                  <PortalNavLinks
                    variant="header"
                    showLoginLink={showLoginLink}
                    headerConfigured={configuredHeaderNav}
                  />
                )}
              </div>
            </div>
          </header>
            }
          />

          <main
            id="main-content"
            role="main"
            tabIndex={-1}
            className="mx-auto w-full max-w-6xl flex-1 px-4 pt-4 pb-[calc(env(safe-area-inset-bottom)+var(--brand-bottom-nav-height)+1.5rem)] outline-none sm:px-6 sm:pt-8 lg:pb-8"
          >
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </main>

          <ThemeSlot
            name="shell.footer"
            fallback={
          <footer
            className="mt-auto border-t border-[var(--brand-border-soft)] bg-[var(--brand-background)] pt-8 pb-[calc(env(safe-area-inset-bottom)+var(--brand-bottom-nav-height)+1.5rem)] text-[var(--brand-text-secondary)] lg:pb-8"
            role="contentinfo"
          >
            <div
              className={cn(
                'mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 sm:grid-cols-2',
                footerSecondaryNav.length > 0 ? 'lg:grid-cols-[1.3fr_1fr_1fr]' : 'lg:grid-cols-[1.3fr_1fr]',
              )}
            >
              <div className="space-y-2">
                <Link to="/" className="inline-flex items-center gap-3 text-[var(--brand-text-primary)] no-underline">
                  {brandMerged?.logo_url ? (
                    <img
                      src={resolveAssetUrl(brandMerged.logo_url)}
                      alt={t('a11y.logo.alt', { brand: brandName })}
                      className="h-auto max-h-8 w-auto max-w-28 object-contain"
                    />
                  ) : (
                    <span className="font-display text-lg leading-none">{brandName}</span>
                  )}
                </Link>
              </div>

              <nav aria-label={t('legal.footer_nav')} className="w-full">
                <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[var(--brand-text-secondary)]">
                  {t('horizon.footer.legal_heading')}
                </p>
                <ul className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2 [&_a]:inline-flex [&_a]:min-h-6 [&_a]:items-center [&_button]:inline-flex [&_button]:min-h-6 [&_button]:items-center">
                  <li>
                    <Link
                      to="/faq"
                      className="text-[var(--brand-primary-strong)] underline-offset-4 hover:underline"
                      aria-current={portalNavAriaCurrentPage(pathname, '/faq') ? 'page' : undefined}
                    >
                      {t('faq.title')}
                    </Link>
                  </li>
                  {LEGAL_PAGE_SLUGS.map((slug: LegalPageSlug) => (
                    <li key={slug}>
                      <Link
                        to="/legal/$legalSlug"
                        params={{ legalSlug: slug } as never}
                        className="text-[var(--brand-primary-strong)] underline-offset-4 hover:underline"
                      >
                        {t(`legal.link.${slug}`)}
                      </Link>
                    </li>
                  ))}
                  <li>
                    <Link
                      to="/me/gdpr"
                      className="text-[var(--brand-primary-strong)] underline-offset-4 hover:underline"
                      aria-current={portalNavAriaCurrentPage(pathname, '/me/gdpr') ? 'page' : undefined}
                    >
                      {t('profile.gdpr.rights_link')}
                    </Link>
                  </li>
                  <li>
                    <button
                      type="button"
                      className="text-[var(--brand-primary-strong)] underline-offset-4 hover:underline"
                      onClick={() => openCookiePreferences()}
                    >
                      {t('legal.cookies.manage_preferences')}
                    </button>
                  </li>
                </ul>
              </nav>

              {footerSecondaryNav.length > 0 ? (
                <nav aria-label={t('app.layout.footer_secondary_nav')} className="w-full">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[var(--brand-text-secondary)]">
                    {t('app.layout.footer_secondary_nav')}
                  </p>
                  <ul className="flex flex-col gap-2 text-sm [&_a]:inline-flex [&_a]:min-h-6 [&_a]:items-center [&_button]:inline-flex [&_button]:min-h-6 [&_button]:items-center">
                    {footerSecondaryNav.map((item) => {
                      const title = item.link_title?.trim() !== '' ? item.link_title : undefined
                      if (item.link_type === 'internal') {
                        const aria = portalNavAriaCurrentPage(pathname, item.href) ? ('page' as const) : undefined

                        return (
                          <li key={`${item.label}-${item.href}`}>
                            <Link
                              to={item.href}
                              title={title}
                              className="text-[var(--brand-primary-strong)] underline-offset-4 hover:underline"
                              aria-current={aria}
                            >
                              {item.label}
                            </Link>
                          </li>
                        )
                      }

                      const target = item.target !== undefined && item.target !== '' ? item.target : '_blank'
                      const relDefault = target === '_blank' ? 'noopener noreferrer' : undefined
                      const rel = item.rel?.trim() !== '' ? item.rel : relDefault

                      return (
                        <li key={`${item.label}-${item.href}`}>
                          <a
                            href={item.href}
                            title={title}
                            target={target}
                            rel={rel}
                            className="inline-flex items-center gap-1 text-[var(--brand-primary-strong)] underline-offset-4 hover:underline"
                          >
                            {item.label}
                            <ExternalIcon className="horizon-external-icon size-3.5" aria-hidden />
                            <span className="sr-only">{srExternal}</span>
                          </a>
                        </li>
                      )
                    })}
                  </ul>
                </nav>
              ) : null}
            </div>
            <div className="mx-auto mt-6 max-w-6xl border-t border-[var(--brand-border-soft)] px-4 py-3 text-center text-xs text-[var(--brand-text-secondary)] sm:px-6 sm:text-left">
              <p>
                {t('horizon.footer.copyright', {
                  year: new Date().getFullYear(),
                  brand: brandName,
                })}
              </p>
              <p className="mt-1">{t('horizon.footer.legal_caption')}</p>
            </div>
          </footer>
            }
          />
        </div>
      </div>

      <ThemeSlot name="shell.bottom_nav" fallback={<BottomNav />} />
    </div>
  )
}
